import React, { FunctionComponent, useMemo, useRef } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';
import { useRouter } from 'next/router';

import { CategoryTreeRootItem } from '@domains/categories';
import { SocialContact, SocialContactType } from '@domains/contacts';

import { $cartDetails, $totalCartProducts } from '@stores/cart';
import { setIsOpenLogin } from '@stores/dialogs';
import { useCommonData, useGlobalData } from '@stores/global-data';
import { setIsOpenCatalogMenu, useHeaderStore } from '@stores/header';
import { setLoginAction } from '@stores/login-action';
import { $profile } from '@stores/profile';

import { sendGoToCartFromHeaderEvent } from '@utils/analytics';
import { formatPlusSevenPhone, formatRussianPhoneNumber } from '@utils/contacts';
import { CONFETTI } from '@utils/env';
import {
    makeCartRoute,
    makeFavoriteProductsRoute,
    makeHomeRoute,
    makeProfileRoute,
} from '@utils/route';
import { replaceSpecialCharacters } from '@utils/utils';

import { useClickOutside } from '@hooks/click-outside';

import { Container } from '@components/common/Container/Container';
import { CurrencyPresenter } from '@components/common/CurrencyPresenter/CurrencyPresenter';
import { BadgeCount } from '@components/common/Header/BadgeCount/BadgeCount';
import { CatalogWidget } from '@components/common/Header/CatalogWidget/CatalogWidget';
import { MenuButton } from '@components/common/Header/MenuButton/MenuButton';
import { MobileCatalogWidget } from '@components/common/Header/MobileCatalogWidget/MobileCatalogWidget';
import { SearchWidget } from '@components/common/Header/SearchWidget/SearchWidget';
import { SocialContacts } from '@components/common/Header/SocialContacts/SocialContacts';
import { Topline } from '@components/common/Header/Topline/Topline';
import { Link } from '@components/common/Link/Link';
import { TelegramPromo } from '@components/common/TelegramPromo/TelegramPromo';
import { CartIcon, FavoriteIcon, ProfileIcon, SiteLogoIcon } from '@components/icons';

import styles from './Header.module.scss';

const MAX_CART_BADGE_COUNT = 99;

export const Header: FunctionComponent = () => {
    const router = useRouter();

    const { isOpenCatalogMenu } = useHeaderStore();

    const [setIsOpenLoginFn, setLoginActionFn] = useUnit([setIsOpenLogin, setLoginAction]);
    const { user } = useUnit($profile);
    const setIsOpenCatalogMenuFn = useUnit(setIsOpenCatalogMenu);

    const isLoggedIn = user !== null;

    const {
        menu: { header: headerMenu },
        contacts: {
            header: { phone: contactPhone },
        },
    } = useCommonData();

    const {
        categoriesList: { items: categories },
    } = useGlobalData();

    const categoriesTree = useMemo(
        () =>
            categories
                .map(
                    (category): CategoryTreeRootItem => ({
                        ...category,
                        name: replaceSpecialCharacters(category.name, ' '),
                        subcategories: categories.filter(
                            ({ parent_id }) => parent_id === category.id,
                        ),
                    }),
                )
                .filter(({ parent_id }) => parent_id === null),
        [categories],
    );

    const [{ items_price_with_discounts, items }, totalCartProducts] = useUnit([
        $cartDetails,
        $totalCartProducts,
    ]);
    const isCartEmpty = items.length === 0;
    const isShowCartBadge = totalCartProducts > 0;
    const cartBadgeCount =
        totalCartProducts > MAX_CART_BADGE_COUNT
            ? `${MAX_CART_BADGE_COUNT}+`
            : `${totalCartProducts}`;

    const handleUserNotAuthorized = () => setIsOpenLoginFn(true);

    const delayedRedirect = (route: string) => () => {
        setTimeout(() => router.push(route), 300);
    };

    const handleAuthorizationModalWithProfileRedirect = () => {
        handleUserNotAuthorized();
        setLoginActionFn(delayedRedirect(makeProfileRoute()));
    };
    const handleAuthorizationModalWithFavoritesRedirect = () => {
        handleUserNotAuthorized();
        setLoginActionFn(delayedRedirect(makeFavoriteProductsRoute()));
    };

    const catalogWidget = useRef<HTMLDivElement>(null);

    useClickOutside({
        ref: catalogWidget,
        action: () => {
            if (isOpenCatalogMenu) setIsOpenCatalogMenuFn(false);
        },
    });

    const contacts: SocialContact[] = [
        {
            type: SocialContactType.Max,
            href: 'https://max.ru/u/f9LHodD0cOKdcCgFs8sbrMVSxjxT9M1v-zKM9SS_A-2CsA2VjLzK0VJNOg8',
            ariaLabel: 'Написать в Max',
        },
        {
            type: SocialContactType.Telegram,
            href: 'https://t.me/+79147015457',
            ariaLabel: 'Написать в Telegram',
        },
    ];

    if (contactPhone !== null) {
        contacts.push({
            type: SocialContactType.Phone,
            href: `tel:+${formatPlusSevenPhone(contactPhone)}`,
            ariaLabel: `Позвонить по номеру ${formatRussianPhoneNumber(contactPhone)}`,
        });
    }

    return (
        <div className={styles.root}>
            <header className={clsx(styles.header, isOpenCatalogMenu && styles.isMenuOpen)}>
                <Container>
                    <div className={styles.topRow}>
                        <SocialContacts contacts={contacts} />
                        <div className={styles.navigation}>
                            {headerMenu.map((item, index) => (
                                <Link
                                    key={index}
                                    route={item.url}
                                    className={styles.navigationLink}
                                    target={item.open_in_new_tab ? '_blank' : '_self'}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </Container>
                <hr className={styles.separator} />
                <Container classes={{ root: styles.mainContainer }}>
                    <div className={styles.bottomRow}>
                        <div className={styles.hamburgerColumn}>
                            <MenuButton isOpenMenu={isOpenCatalogMenu} />
                        </div>
                        <div className={styles.logoColumn}>
                            <Link
                                route={makeHomeRoute()}
                                className={styles.homeLink}
                                data-testid="header-logo"
                            >
                                <SiteLogoIcon className={styles.logo} />
                            </Link>
                        </div>
                        <div className={styles.searchColumn}>
                            {CONFETTI && (
                                <>
                                    <img
                                        src="/images/confetti/confetti-search-top-right.svg"
                                        className={styles.searchConfettiTopRight}
                                        alt=""
                                        loading="lazy"
                                        width={36}
                                        height={32}
                                    />
                                </>
                            )}
                            <SearchWidget />
                        </div>
                        <div className={styles.buttonsColumn}>
                            {isLoggedIn ? (
                                <Link
                                    route={makeFavoriteProductsRoute()}
                                    className={clsx(styles.button, styles.isActive)}
                                    aria-label="Перейти к избранным товарам"
                                >
                                    <FavoriteIcon name="favorite" className={styles.buttonIcon} />
                                    <div className={styles.caption}>Избранное</div>
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    className={clsx(styles.button)}
                                    onClick={handleAuthorizationModalWithFavoritesRedirect}
                                >
                                    <FavoriteIcon name="favorite" className={styles.buttonIcon} />
                                    <div className={styles.caption}>Избранное</div>
                                </button>
                            )}

                            <Link
                                className={clsx(styles.button, !isCartEmpty && styles.isActive)}
                                route={makeCartRoute()}
                                onClick={sendGoToCartFromHeaderEvent}
                                aria-label="Перейти в корзину"
                                data-testid="header-cart-link"
                            >
                                <BadgeCount isShowCount={isShowCartBadge} count={cartBadgeCount}>
                                    <CartIcon
                                        name={isCartEmpty ? 'cart' : 'cart-filled'}
                                        className={clsx(
                                            styles.buttonIcon,
                                            !isCartEmpty && styles.isFilled,
                                        )}
                                    />
                                </BadgeCount>
                                <div className={styles.caption}>
                                    {isCartEmpty ? (
                                        'Корзина'
                                    ) : (
                                        <CurrencyPresenter amount={items_price_with_discounts} />
                                    )}
                                </div>
                            </Link>

                            {isLoggedIn ? (
                                <Link
                                    route={makeProfileRoute()}
                                    className={clsx(styles.button, styles.isActive)}
                                    aria-label="Перейти в профиль"
                                    data-testid="header-profile-link"
                                >
                                    <ProfileIcon
                                        name="profile-filled"
                                        className={clsx(styles.buttonIcon, styles.isFilled)}
                                    />
                                    <div className={styles.caption}>{user.name || 'Профиль'}</div>
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.button}
                                    onClick={handleAuthorizationModalWithProfileRedirect}
                                    data-testid="header-login-btn"
                                >
                                    <ProfileIcon name="profile" className={styles.buttonIcon} />
                                    <div className={styles.caption}>Войти</div>
                                </button>
                            )}
                        </div>
                    </div>
                </Container>
                <Container classes={{ root: styles.confettiContainer }}>
                    {CONFETTI && (
                        <>
                            <img
                                src="/images/confetti/confetti-logo.svg"
                                className={styles.logoConfetti}
                                alt=""
                                loading="lazy"
                                width={157}
                                height={78}
                            />
                            <img
                                className={styles.buttonsConfetti}
                                alt=""
                                src="/images/confetti/confetti-actions.svg"
                                loading="lazy"
                                width={260}
                                height={67}
                            />
                            <img
                                src="/images/confetti/confetti-search-bottom-left.svg"
                                className={styles.searchConfettiBottomLeft}
                                alt=""
                                loading="lazy"
                                width={36}
                                height={22}
                            />
                        </>
                    )}
                </Container>
            </header>
            <Topline />
            <TelegramPromo />

            <div ref={catalogWidget}>
                <CatalogWidget isOpen={isOpenCatalogMenu} categories={categoriesTree} />
                <MobileCatalogWidget
                    isOpen={isOpenCatalogMenu}
                    categories={categoriesTree}
                    navigation={headerMenu}
                    contacts={contacts}
                />
            </div>
        </div>
    );
};
